import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationFunctionType } from '@/shared/globalTypes';
import { TextCopy } from '@/shared/ui';
import { QrCode } from '@/shared/ui/QrCode';
import { useClipboard } from '@/shared/ui/TextCopy/useClipboard';

import { ChannelStepsList } from '../scanQrCode/ChannelStepsList';
import { ContentAndQr } from '../scanQrCode/ContentAndQr';
import { WarningTextAboutDialogs } from '../scanQrCode/WarningTextAboutDialogs';

interface ChannelQrCodeContentProps {
  qr?: string;
  qrExpires?: string;
  id: string;
}

const getSteps = (t: TranslationFunctionType) => [
  t('channels:tgPersonal.stepsList.step1'),
  t('channels:tgPersonal.stepsList.step2'),
  t('channels:tgPersonal.stepsList.step3'),
];

export const AuthByQrCode: React.FC<ChannelQrCodeContentProps> = ({
  qr,
  qrExpires,
  id,
}) => {
  const { t } = useTranslation();
  const { copyToClipboard } = useClipboard();
  const [nowMs, setNowMs] = useState(() => Date.now());
  const expiresAtMs = qrExpires ? Date.parse(qrExpires) : Number.NaN;
  const qrForDisplay = Number.isFinite(expiresAtMs) && expiresAtMs > nowMs
    ? qr
    : undefined;

  useEffect(() => {
    const currentTimeMs = Date.now();
    setNowMs(currentTimeMs);

    if (!Number.isFinite(expiresAtMs) || expiresAtMs <= currentTimeMs) {
      return;
    }

    const timer = window.setTimeout(() => {
      setNowMs(Date.now());
    }, expiresAtMs - currentTimeMs);

    return () => window.clearTimeout(timer);
  }, [expiresAtMs]);

  const handleCopyLink = async () => {
    const link = `${window.location.origin}/qr/${id}`;
    await copyToClipboard(link);
  };

  return (
    <ContentAndQr
      contentSlot={<ChannelStepsList steps={getSteps(t)} />}
      underContentSlot={<WarningTextAboutDialogs />}
      qrSlot={<QrCode qrData={qrForDisplay} />}
      underQrSlot={
        <TextCopy
          label={t('channels:copyQrCode')}
          onClick={handleCopyLink}
          sx={{ cursor: 'pointer', textAlign: 'center' }}
          color="primary"
        />
      }
    />
  );
};
